import { Controller } from "@hotwired/stimulus"
import { logEvent } from "../field_logger"
import { recordLedgerEntry, ACTIONS } from "../field_response_ledger"

// Handles the scope toggle on the response-level schedule sheet.
// When the "Apply to all" checkbox is ticked, the form action switches from
// the individual response schedule URL to the place schedule URL so that
// the server schedules every unscheduled mission at this store instead of just one.
//
// Also logs schedule submissions to the field diagnostic log for support tracing.
//
// Usage:
//   data-controller="field-schedule"
//   data-field-schedule-single-url-value="..."      -- response schedule path
//   data-field-schedule-place-url-value="..."       -- place schedule path
//   data-field-schedule-response-id-value="..."     -- this response's id (card #4688 ledger)
//   data-field-schedule-target="form"               -- the <form> element
//   data-field-schedule-target="hint"               -- the explanatory <p> text
export default class extends Controller {
  static targets = ["form", "hint"]
  static values  = { singleUrl: String, placeUrl: String, responseId: Number }

  connect() {
    if (this.hasFormTarget) {
      this._onSubmitStart = this._logSubmit.bind(this)
      this._onSubmitEnd   = this._recordLedger.bind(this)
      this.formTarget.addEventListener("turbo:submit-start", this._onSubmitStart)
      this.formTarget.addEventListener("turbo:submit-end", this._onSubmitEnd)
    }
  }

  disconnect() {
    if (this.hasFormTarget) {
      if (this._onSubmitStart) this.formTarget.removeEventListener("turbo:submit-start", this._onSubmitStart)
      if (this._onSubmitEnd)   this.formTarget.removeEventListener("turbo:submit-end", this._onSubmitEnd)
    }
  }

  // Card #4688 -- one ledger entry per single-response schedule write. The
  // bulk "apply to all" submit (place URL) reschedules every unscheduled
  // response at the store -- there is no single response id to attribute
  // that write to, so it is skipped here (the existing _logSubmit event
  // above still records it to the general diagnostic log).
  _recordLedger(event) {
    const isPlace = this.hasFormTarget && this.formTarget.action === this.placeUrlValue
    if (isPlace || !this.hasResponseIdValue) return

    const httpResponse = event.detail?.fetchResponse?.response || null
    recordLedgerEntry(this.responseIdValue, {
      action:            ACTIONS.SCHEDULE,
      localStatusAfter:  event.detail?.success ? "scheduled" : null,
      httpStatus:        httpResponse?.status ?? null,
      serverStatus:      httpResponse?.headers?.get?.("X-WIN-Response-Status") || null,
      requestId:         httpResponse?.headers?.get?.("X-Request-Id") || null
    }).catch(() => {})
  }

  toggleScope(e) {
    const allStores = e.target.checked
    this.formTarget.action = allStores ? this.placeUrlValue : this.singleUrlValue
    if (this.hasHintTarget) {
      this.hintTarget.textContent = allStores
        ? "Sets a visit date for all unscheduled missions at this store."
        : "Sets a visit date for this mission only."
    }
  }

  _logSubmit() {
    const isPlace = this.hasFormTarget && this.formTarget.action === this.placeUrlValue
    logEvent("schedule", isPlace ? "Place scheduled (all missions)" : "Response scheduled", {
      url: this.hasFormTarget ? this.formTarget.action : null
    }).catch(() => {})
  }
}
